import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, ShieldOff, Loader2, CreditCard } from 'lucide-react';
import api from '../api/client';
import { useCurrency } from '../context/CurrencyContext';

export default function PlatinumVerifyPage() {
  const [searchParams] = useSearchParams();
  const { formatDate } = useCurrency();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const qr = searchParams.get('qr');
    if (!qr) {
      setError('No QR data provided');
      setLoading(false);
      return;
    }
    api.get('/api/membership/platinum/verify-public', { params: { qr } })
      .then(res => setResult(res.data))
      .catch(err => setError(err.displayMessage || 'Verification failed'))
      .finally(() => setLoading(false));
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-[#2d1f3d] flex items-center justify-center" data-testid="platinum-verify-loading">
        <Loader2 size={40} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-[#2d1f3d] flex items-center justify-center p-4" data-testid="platinum-verify-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-[#D4AF37] text-2xl font-light tracking-[6px] uppercase">Platinum</h1>
          <p className="text-gray-500 text-xs tracking-[3px] uppercase mt-1">Member Verification</p>
        </div>

        {error ? (
          <div className="bg-[#2a2a2a] rounded-2xl p-8 text-center border border-red-900/30" data-testid="platinum-verify-error">
            <ShieldOff size={48} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-white text-lg font-medium mb-2">Verification Failed</h2>
            <p className="text-gray-400 text-sm">{error}</p>
          </div>
        ) : result ? (
          <div className="bg-[#2a2a2a] rounded-2xl p-8 border border-[#D4AF37]/20" data-testid="platinum-verify-success">
            <div className="text-center mb-6">
              <ShieldCheck size={48} className="text-emerald-400 mx-auto mb-3" />
              <h2 className="text-white text-lg font-medium">Verified Platinum Member</h2>
            </div>
            <div className="space-y-3 bg-[#1a1a1a] rounded-xl p-5 border border-gray-800">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Name</span>
                <span className="text-white font-medium">{result.member_name}</span>
              </div>
              <div className="border-t border-gray-800" />
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Tier</span>
                <span className="text-[#D4AF37] font-medium uppercase">{result.tier}</span>
              </div>
              <div className="border-t border-gray-800" />
              {result.card_number && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Card</span>
                    <span className="text-gray-300 font-mono text-sm tracking-wider">
                      {result.card_number.replace(/(.{4})/g, '$1 ').trim()}
                    </span>
                  </div>
                  <div className="border-t border-gray-800" />
                </>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Status</span>
                <span className="text-emerald-400 font-medium capitalize">{result.status}</span>
              </div>
              {result.expiry_date && (
                <>
                  <div className="border-t border-gray-800" />
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Valid Until</span>
                    <span className="text-gray-300 text-sm">{formatDate(result.expiry_date)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
